'use client';

import {
  resolveLayout,
  SquishEngine,
  type SquishExpression,
  type SquishLayout,
} from '@usespaceui/squishmoji';
import { useEffect, useId, useMemo, useRef } from 'react';
import type { YourImaiPersona } from '@/components/your-imai/your-imai-persona';
import { cn } from '@/lib/utils';
import {
  getListeningFrame,
  getVoiceColor,
  voiceAccentColor,
  voiceBaseColorForSeed,
  voiceColorCss,
  voiceGradientColor,
  type VoiceAvatarMode,
  type VoiceColor,
} from './voice-companion-colors';
import {
  voiceEyeColor,
  voiceTransitionColor,
  voiceTransitionStart,
} from './voice-eye-color';

/** The member's Your IMAI look the call avatar is drawn in. */
export type VoiceAvatarLook = Pick<
  YourImaiPersona,
  'shape' | 'expression' | 'colorSeed'
>;

/**
 * A hairline halo in the page's ink, only where the body would melt into
 * the page: near-black bodies on the dark theme, very light ones on the
 * light theme. Every other body stays bare like the sidebar blob.
 */
const DARK_THEME_HALO_CLASS =
  'dark:[filter:drop-shadow(0_0_0.75px_color-mix(in_oklab,var(--foreground)_35%,transparent))]';
const LIGHT_THEME_HALO_CLASS =
  '[filter:drop-shadow(0_0_0.75px_color-mix(in_oklab,var(--foreground)_35%,transparent))] dark:[filter:none]';

export function voiceFaceHaloClass(base: VoiceColor): string | undefined {
  if (base[0] < 0.35) return DARK_THEME_HALO_CLASS;
  if (base[0] > 0.82) return LIGHT_THEME_HALO_CLASS;
  return undefined;
}
/** One scale for every shape, so bodies keep the sidebar's proportions. */
const BODY_SCALE = 13 / 112;
const EXPRESSIONS: readonly SquishExpression[] = [
  'attentive',
  'curious',
  'happy',
  'amazed',
  'excited',
  'proud',
];

/** The face each mode settles on; idle shows the member's own face. */
function modeExpression(
  mode: VoiceAvatarMode,
  look: VoiceAvatarLook
): SquishExpression {
  if (mode === 'speaking') return 'excited';
  if (mode === 'listening') return 'curious';
  return look.expression;
}

/**
 * Eye transforms at squishmoji's own 3-decimal precision, so server and
 * browser float noise can't make the markup differ.
 */
function eyeMatrix(matrix: readonly number[]): string {
  return matrix.map((value) => Math.round(value * 1000) / 1000).join(' ');
}

function movingEyes(
  eyes: SquishLayout['eyes'],
  time: number
): SquishLayout['eyes'] {
  const phase = (time + 1.35) % 4.7;
  const lid = phase > 0.18 ? 1 : Math.max(0.035, Math.abs(phase / 0.09 - 1));
  return eyes.map((eye) => {
    const matrix: typeof eye.matrix = [...eye.matrix];
    matrix[3] *= lid;
    matrix[4] += Math.sin(time * 0.64 + 0.8) * 12.4;
    matrix[5] += Math.sin(time * 0.43 + 2.2) * 9.6;
    return { ...eye, matrix };
  });
}

export function VoiceCompanionFace({
  mode,
  running,
  look,
}: {
  mode: VoiceAvatarMode;
  running: boolean;
  look: VoiceAvatarLook;
}) {
  const gradientId = useId();
  const pathRef = useRef<SVGPathElement>(null);
  const groupRef = useRef<SVGGElement>(null);
  const eyesRef = useRef<SVGGElement>(null);
  const gradientRef = useRef<SVGLinearGradientElement>(null);
  const { colorSeed, shape } = look;
  const expression = modeExpression(mode, look);
  const base = voiceBaseColorForSeed(colorSeed);
  const initialColor = getVoiceColor(mode, 0, base);
  const currentColor = useRef(initialColor);
  // Mode and look together: a new persona repaints like a new mode does.
  const stateKey = `${mode}|${colorSeed}|${shape}|${expression}`;
  const previousKey = useRef<string | null>(null);
  const previousEye = useRef<string | null>(null);
  const previousSeed = useRef<string | null>(null);
  const elapsedTime = useRef(0);
  const animationState = useRef<{
    engine: SquishEngine;
    morphAt: number;
  } | null>(null);
  const still = useMemo(
    () => resolveLayout(colorSeed, { shape, expression }),
    [colorSeed, shape, expression]
  );

  useEffect(() => {
    const path = pathRef.current;
    const group = groupRef.current;
    const eyes = eyesRef.current;
    if (!path || !group || !eyes) return;
    const host = path.closest<HTMLElement>(
      '[data-slot="voice-companion-face"]'
    );
    const changed = previousKey.current !== stateKey;
    const eye = voiceEyeColor(mode, base);
    const transitionFrom =
      previousKey.current && changed
        ? voiceTransitionStart(
            currentColor.current,
            previousEye.current,
            eye,
            previousSeed.current === colorSeed
          )
        : null;
    previousKey.current = stateKey;
    previousEye.current = eye;
    previousSeed.current = colorSeed;
    if (changed) {
      elapsedTime.current = 0;
      animationState.current = null;
    }

    const paint = (layout: SquishLayout, timeMs: number, animate: boolean) => {
      const color = voiceTransitionColor(
        animate ? transitionFrom : null,
        mode,
        timeMs,
        base
      );
      currentColor.current = color;
      if (host) host.style.color = voiceColorCss(voiceAccentColor(color));
      const offsets = [-1, 0, 1] as const;
      offsets.forEach((offset, index) =>
        gradientRef.current?.children[index]?.setAttribute(
          'stop-color',
          voiceColorCss(voiceGradientColor(color, offset))
        )
      );
      path.setAttribute('d', layout.body);
      eyes.setAttribute('fill', eye);
      layout.eyes.forEach((eye, index) => {
        const rect = eyes.children[index];
        if (!rect) return;
        rect.setAttribute('x', String(-eye.width / 2));
        rect.setAttribute('y', String(-eye.height / 2));
        rect.setAttribute('width', String(eye.width));
        rect.setAttribute('height', String(eye.height));
        rect.setAttribute('rx', String(Math.min(eye.width, eye.height) / 2));
        rect.setAttribute('transform', `matrix(${eyeMatrix(eye.matrix)})`);
      });
      const time = timeMs / 1000;
      group.setAttribute(
        'transform',
        animate && mode === 'listening'
          ? `translate(${16 + Math.sin(time * 1.7) * 0.35} ${16 + Math.cos(time * 1.3) * 0.35}) rotate(${Math.sin(time) * 3}) scale(${BODY_SCALE * (1 + Math.sin(time * 2.1) * 0.035)})`
          : `translate(16 16) scale(${BODY_SCALE})`
      );
    };

    if (!running || mode === 'idle') {
      if (changed) paint(still, 0, false);
      return;
    }

    const animation = animationState.current ?? {
      engine: new SquishEngine(colorSeed, {
        shape,
        expression: mode === 'speaking' ? 'excited' : 'attentive',
      }),
      morphAt: 0,
    };
    animationState.current = animation;
    const { engine } = animation;
    const started = performance.now();
    const resumedAt = elapsedTime.current;
    let frame = 0;
    const draw = (now: number) => {
      const timeMs = resumedAt + now - started;
      elapsedTime.current = timeMs;
      const time = timeMs / 1000;
      if (mode === 'listening') {
        const step = getListeningFrame(timeMs);
        if (step.morphAt !== animation.morphAt) {
          animation.morphAt = step.morphAt;
          engine.setExpression(
            EXPRESSIONS[step.index % EXPRESSIONS.length]!,
            step.morphAt / 1000,
            0.28
          );
        }
      }
      // The body keeps the member's shape in every mode; listening cycles
      // faces and sways, speaking only animates fitted eyes.
      paint(
        engine.layout(time, (fitted) => movingEyes(fitted, time)),
        timeMs,
        true
      );
      frame = requestAnimationFrame(draw);
    };
    draw(started);
    return () => cancelAnimationFrame(frame);
  }, [base, colorSeed, mode, running, shape, stateKey, still]);

  return (
    <svg
      viewBox="0 0 32 32"
      className={cn('size-16 overflow-visible', voiceFaceHaloClass(base))}
      data-voice-avatar-mode={mode}
      data-voice-avatar-shape={shape}
      data-voice-avatar-seed={colorSeed}
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id={gradientId}
          ref={gradientRef}
          x1="0"
          y1="0"
          x2="1"
          y2="1"
        >
          <stop
            offset="0%"
            stopColor={voiceColorCss(voiceGradientColor(initialColor, -1))}
          />
          <stop offset="50%" stopColor={voiceColorCss(initialColor)} />
          <stop
            offset="100%"
            stopColor={voiceColorCss(voiceGradientColor(initialColor, 1))}
          />
        </linearGradient>
      </defs>
      <g ref={groupRef} transform={`translate(16 16) scale(${BODY_SCALE})`}>
        <path
          ref={pathRef}
          data-agent-body="true"
          d={still.body}
          fill={`url(#${gradientId})`}
          stroke={`url(#${gradientId})`}
          strokeWidth={1.5 / BODY_SCALE}
        />
        <g
          ref={eyesRef}
          data-agent-animated-eyes="true"
          fill={voiceEyeColor(mode, base)}
        >
          {still.eyes.map((eye, index) => (
            <rect
              key={index}
              x={-eye.width / 2}
              y={-eye.height / 2}
              width={eye.width}
              height={eye.height}
              rx={Math.min(eye.width, eye.height) / 2}
              transform={`matrix(${eyeMatrix(eye.matrix)})`}
            />
          ))}
        </g>
      </g>
    </svg>
  );
}
